import { Link } from 'react-router-dom'
import { roles } from '../data/roles.ts'
import { skills } from '../data/skills.ts'
import { TrendingUp, GraduationCap } from 'lucide-react'
import SeoH1 from '../components/SeoH1.tsx'

const calculators = [
  { path: '/ai-salary-gap', title: 'AI Salary Gap Calculator', desc: 'Your role-specific AI wage premium.' },
  { path: '/skills-roi', title: 'Skills ROI Calculator', desc: 'Return on any AI skill investment.' },
  { path: '/ai-productivity', title: 'AI Productivity Value Calculator', desc: 'Hours saved per week, in dollars.' },
  { path: '/hire-vs-ai', title: 'Hire vs AI Calculator', desc: 'Hire a person or automate with AI.' },
  { path: '/automation-savings', title: 'Automation Savings Calculator', desc: 'Payback and ROI of process automation.' },
  { path: '/displacement-risk', title: 'AI Displacement Risk Calculator', desc: 'Income at risk in your career.' },
  { path: '/lifetime-earnings', title: 'Lifetime Earnings Calculator', desc: 'Career NPV with and without AI upskilling.' },
  { path: '/ai-tax-deductions', title: 'AI Tax Deduction Calculator', desc: 'Deduct AI tools and reduce your tax bill.' },
  { path: '/ai-freelance-rate', title: 'AI Freelance Rate Calculator', desc: 'Charge for AI-augmented freelance output.' },
  { path: '/ai-subscription-roi', title: 'AI Subscription ROI Calculator', desc: 'Is ChatGPT Plus or Claude Pro worth it?' },
  { path: '/ai-raise-calculator', title: 'AI Raise Negotiation Calculator', desc: 'Turn AI savings into a raise argument.' },
  { path: '/ai-side-hustle', title: 'AI Side Hustle Profitability Calculator', desc: 'Profitability timeline for AI side hustles.' },
  { path: '/ai-api-cost', title: 'AI API Cost Estimator', desc: 'API bill at 1K / 10K / 100K users.' },
  { path: '/ai-vs-offshore', title: 'AI vs. Offshore Staff Calculator', desc: 'AI stack vs. offshore cost.' },
  { path: '/ai-training-roi', title: 'AI Team Training ROI Calculator', desc: 'Team AI training payback period.' },
  { path: '/ai-content-roi', title: 'AI Content ROI Calculator', desc: 'AI content vs. agency or freelancer.' },
  { path: '/ai-consulting-rate', title: 'AI Consulting Rate Calculator', desc: 'Hourly or project rate for AI consultants.' },
  { path: '/ai-revenue-impact', title: 'AI Business Revenue Impact Calculator', desc: 'Top-line impact of AI features.' },
  { path: '/ai-job-search', title: 'AI Job Search Accelerator Calculator', desc: 'More callbacks and salary lift.' },
  { path: '/ai-pricing-elasticity', title: 'AI Pricing Elasticity Calculator', desc: 'What to charge for an AI product feature.' },
  { path: '/ai-compliance-cost', title: 'AI Ethics & Compliance Cost Calculator', desc: 'EU AI Act and governance cost.' },
  { path: '/ai-team-sizing', title: 'AI-Augmented Team Size Calculator', desc: 'Headcount planning with AI variables.' },
  { path: '/ai-error-cost', title: 'AI Hallucination Cost Calculator', desc: 'What AI errors actually cost your workflow.' },
  { path: '/ai-support-roi', title: 'AI Customer Service ROI Calculator', desc: 'AI chatbot vs. human support agent.' },
  { path: '/ai-tool-budget', title: 'Personal AI Budget Calculator', desc: 'How much to spend on AI subscriptions.' },
  { path: '/ai-healthcare-admin', title: 'AI Healthcare Admin Savings Calculator', desc: 'Savings per clinician per year.' },
  { path: '/ai-portfolio-exposure', title: 'AI Stock Portfolio Exposure Calculator', desc: 'Portfolio risk/upside from AI disruption.' },
  { path: '/ai-legal-cost', title: 'AI Legal Cost Reduction Calculator', desc: 'AI contract review vs. outside counsel.' },
  { path: '/ai-recruiting-roi', title: 'AI Recruiting ROI Calculator', desc: 'Time and cost per hire with AI.' },
  { path: '/ai-ad-creative-roi', title: 'AI Ad Creative ROI Calculator', desc: 'AI-generated creative vs. agency spend.' },
  { path: '/ai-carbon-cost', title: 'AI Carbon Footprint Calculator', desc: 'Carbon cost of your AI usage.' },
  { path: '/ai-coding-productivity', title: 'AI Coding Productivity Calculator', desc: 'Hours saved by AI coding tools.' },
  { path: '/local-vs-cloud-ai', title: 'Local AI vs. Cloud AI Cost Calculator', desc: 'CapEx vs. OpEx over 3-5 years.' },
  { path: '/rag-vs-fine-tuning', title: 'RAG vs. Fine-Tuning ROI Calculator', desc: 'RAG or fine-tune your own model.' },
  { path: '/data-readiness-cost', title: 'Data Readiness Cost Calculator', desc: 'Clean data before AI ingestion.' },
  { path: '/multi-agent-workflow', title: 'Multi-Agent Workflow Cost Estimator', desc: 'Hidden token cost of agent systems.' },
  { path: '/ai-voice-video', title: 'AI Voice & Video Generation Estimator', desc: 'Cost per minute of AI media.' },
  { path: '/saas-vs-custom-agent', title: 'SaaS vs. Custom Agent ROI', desc: 'Buy SaaS or build your own agent.' },
  { path: '/lead-response-roi', title: 'Lead Response AI ROI', desc: 'Revenue lift from faster lead response.' },
  { path: '/data-degradation-saver', title: 'AI Data Degradation Saver', desc: 'Revenue saved by clean CRM data.' },
  { path: '/ai-cyber-risk', title: 'AI Cyber/Phishing Risk Calculator', desc: 'Financial risk of AI-driven attacks.' },
  { path: '/ai-career-transition', title: 'AI Career Transition Cost', desc: 'Cost to upskill in AI and re-enter.' },
  { path: '/rto-vs-ai-nomad', title: 'RTO vs. AI Nomad Calculator', desc: 'Remote work plus AI productivity.' },
  { path: '/ai-copyright-ip-risk', title: 'AI Copyright & IP Risk Calculator', desc: 'Financial exposure from AI-generated IP.' },
  { path: '/ai-hardware-upgrade-roi', title: 'AI Hardware Upgrade ROI Calculator', desc: 'Local machine vs. cloud subscription.' },
  { path: '/ai-gig-arbitrage', title: 'AI Gig Marketplace Arbitrage Calculator', desc: 'Hourly vs. AI-augmented flat-rate agency.' },
  { path: '/employee-ai-reimbursement', title: 'Employee AI Expense Reimbursement Calculator', desc: 'Business case to get AI tools reimbursed.' },
  { path: '/enterprise-report-builder', title: 'Enterprise AI Readiness Report Builder', desc: 'White-labeled report for HR leaders.' }
]

const renderGroup = (group: { path: string; title: string; desc: string }[]) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {group.map(calc => (
      <Link
        key={calc.path}
        to={calc.path}
        className="block p-4 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
      >
        <div className="font-medium text-slate-900">{calc.title}</div>
        <div className="text-sm text-slate-600">{calc.desc}</div>
      </Link>
    ))}
  </div>
)

export default function HubPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <SeoH1 fallback="All AI Calculators, Roles, and Skills" className="text-3xl font-bold text-slate-900 mb-3" />
        <p className="text-slate-600 max-w-2xl">
          Explore every role, skill, and calculator combination. Each page is tuned to a specific career or business decision.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary-600" />
            <h2 className="text-xl font-semibold text-slate-900">By role</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {roles.map(role => (
              <Link
                key={role.id}
                to={`/role/${role.id}`}
                className="text-sm text-slate-700 hover:text-primary-700 hover:underline"
              >
                {role.title} salary gap
              </Link>
            ))}
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap className="w-5 h-5 text-emerald-600" />
            <h2 className="text-xl font-semibold text-slate-900">By skill</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {skills.map(skill => (
              <Link
                key={skill.id}
                to={`/skill/${skill.id}`}
                className="text-sm text-slate-700 hover:text-emerald-700 hover:underline"
              >
                {skill.name} ROI
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">All calculators</h2>
        {renderGroup(calculators)}
      </div>
    </div>
  )
}
